import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  FileBarChart2,
  Network,
  ShieldCheck,
  Sparkles,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TourLauncher } from "@/components/TourLauncher";
import { createSession } from "@/lib/api";

const PILLARS = [
  {
    icon: Network,
    title: "Diagnóstico ABC",
    text:
      "Codificamos cada actividad — su lógica, frecuencia, costo y dependencias — bajo la metodología Activity-Based Costing.",
  },
  {
    icon: Sparkles,
    title: "Diseño de agentes IA",
    text:
      "A partir del diagnóstico, definimos agentes especializados que sustituyen o amplifican al colaborador analizado.",
  },
  {
    icon: ShieldCheck,
    title: "Gobernanza desde el día uno",
    text:
      "Cultura de IA gobernada, escalable y alineada al negocio. Métricas, presupuesto y supervisión definidos antes del despliegue.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onStart = async () => {
    try {
      setLoading(true);
      const data = await createSession();
      const sid = data?.session?.id;
      if (!sid) throw new Error("No se pudo iniciar la sesión");
      navigate(`/consulta?session=${sid}`);
    } catch (e) {
      console.error("createSession failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(
        detail
          ? `No fue posible iniciar la consultoría: ${detail}`
          : "No fue posible iniciar la consultoría. Intente de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-white text-ink" data-testid="landing-page">
      {/* HERO - dark navy gradient à la inviktus.com */}
      <section
        className="relative isolate min-h-[92vh] overflow-hidden bg-gradient-hero text-slate-50"
        data-testid="hero-section"
      >
        <div className="bg-dot-grid absolute inset-0 opacity-80" aria-hidden />
        <div className="grain-overlay" />

        <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 pt-7 lg:px-10">
          <InviktusLogo size={36} tone="light" />
          <div className="hidden items-center gap-6 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-300 md:flex">
            <a
              href="/dashboard"
              className="transition hover:text-white"
              data-testid="nav-dashboard-hero"
            >
              Dashboard
            </a>
            <span className="text-slate-600">·</span>
            <span>Latinoamérica</span>
            <span className="text-slate-600">·</span>
            <span>Consultoría IA</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <TourLauncher variant="dark" />
          </div>
        </header>

        <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 pt-24 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:pt-32">
          <div className="lg:col-span-7">
            <p className="eyebrow reveal text-accent-blue-light" data-testid="hero-eyebrow">
              AI Consulting · Activity-Based Costing
            </p>
            <h1
              className="reveal reveal-delay-1 mt-6 font-display text-5xl font-light leading-[1.02] tracking-[-0.02em] text-slate-50 sm:text-6xl lg:text-7xl"
              data-testid="hero-title"
            >
              INVIKTUS,
              <br />
              el{" "}
              <span className="italic font-normal text-gradient-brand">
                multiplicador avanzado
              </span>
              <br />
              de los recursos IA.
            </h1>
            <p
              className="reveal reveal-delay-2 mt-8 max-w-2xl text-base leading-[1.7] text-slate-300/90 sm:text-lg"
              data-testid="hero-description"
            >
              La primera plataforma de acceso empresarial a modelos IA{" "}
              <span className="text-slate-100">gobernados, escalables y seguros</span>.
              Conversación uno a uno con un agente entrenado bajo la disciplina de un partner senior
              que conduce un diagnóstico ABC y entrega su hoja de ruta IA en una sola sesión.
            </p>

            <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-5">
              <Button
                onClick={onStart}
                disabled={loading}
                size="lg"
                className="group h-14 rounded-full bg-brand-gradient px-8 text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-10px_rgba(63,124,255,0.6)] hover:opacity-95"
                data-testid="start-consulting-btn"
              >
                {loading ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : null}
                Iniciar consultoría
                <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <a
                href="#methodology"
                className="text-[11px] font-medium uppercase tracking-[0.22em] text-slate-400 transition hover:text-white"
              >
                Explorar la plataforma →
              </a>
              <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500">
                ~30–45 min · 50 preguntas guiadas
              </span>
            </div>
          </div>

          {/* Right product preview card */}
          <div className="lg:col-span-5">
            <div className="reveal reveal-delay-4 relative ml-auto max-w-md">
              <div
                className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-accent-blue/30 to-accent-purple/20 blur-2xl"
                aria-hidden
              />
              <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-slate-300">
                      Inviktus · ABC Agent
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">live</span>
                </div>
                <div className="mt-5 space-y-4">
                  <div className="flex gap-3">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-gradient text-[10px] font-semibold text-white">
                      AI
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-white/[0.07] px-4 py-3 text-[13px] text-slate-200">
                      Hola, soy su consultor senior. Empecemos por el departamento que quiere
                      mapear bajo ABC.
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <div className="rounded-2xl rounded-tr-sm bg-accent-blue/20 px-4 py-3 text-[13px] text-slate-100 ring-1 ring-accent-blue/40">
                      Atención al cliente. 5 personas, 3 herramientas (CRM, Email, Excel).
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {["Conciliación", "Reportes", "Pagos a proveedores"].map((c) => (
                      <span
                        key={c}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] text-slate-300"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  <span>Latam · 24 meses</span>
                  <span>Powered by Claude</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY (PILLARS) */}
      <section
        id="methodology"
        className="mx-auto max-w-7xl px-6 py-28 lg:px-10"
        data-testid="pillars-section"
      >
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Metodología</p>
            <h2 className="mt-5 font-display text-4xl font-light leading-[1.05] tracking-[-0.02em] text-ink sm:text-5xl">
              Consultoría IA{" "}
              <span className="italic text-gradient-brand">adaptada</span> a cada
              organización.
            </h2>
            <p className="mt-7 text-[15px] leading-[1.7] text-slate-600">
              Analizamos meticulosamente las actividades clave de sus colaboradores bajo el
              paradigma Activity-Based Costing, codificando no sólo el contexto, sino la
              lógica, las interdependencias y el valor de cada tarea.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-slate-200 md:grid-cols-3">
              {PILLARS.map(({ icon: Icon, title, text }, i) => (
                <div
                  key={title}
                  className="lift-on-hover group relative bg-white p-8"
                >
                  <span className="font-mono text-[11px] tracking-[0.22em] text-accent-blue">
                    0{i + 1}
                  </span>
                  <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent-blue/15 to-accent-purple/15 ring-1 ring-accent-blue/20">
                    <Icon
                      className="h-5 w-5 text-accent-blue"
                      strokeWidth={1.6}
                    />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium text-ink">
                    {title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-slate-600">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERABLE */}
      <section className="border-y border-slate-200 bg-slate-50/60">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">Entregable</p>
            <h2 className="mt-5 font-display text-4xl font-light leading-[1.05] tracking-[-0.02em] text-ink sm:text-5xl">
              Un{" "}
              <span className="italic text-gradient-brand">informe ejecutivo</span>{" "}
              descargable en minutos.
            </h2>
            <p className="mt-7 max-w-md text-[15px] leading-[1.7] text-slate-600">
              Al concluir la entrevista, descargue un Excel multi-hoja con todas las respuestas,
              un mapa visual del flujo de procesos (PNG + PDF) y los agentes IA por
              departamento listos para activarse.
            </p>
            <ul className="mt-8 space-y-3 text-[14px] text-slate-700">
              {[
                "Distribución porcentual de actividades (ABC)",
                "Personas, herramientas y micro-tareas por actividad",
                "Diagrama de flujo: Actor → Actividad → Sistemas → Destino",
                "Auto-generación de agentes IA por departamento",
                "Knowledge Base RAG (BM25 + embeddings, citaciones)",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3">
                  <span className="mt-2 inline-block h-1.5 w-4 rounded-full bg-brand-gradient" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="lift-on-hover relative rounded-2xl bg-white p-10 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.15)] ring-1 ring-slate-200">
              <FileBarChart2 className="absolute right-8 top-8 h-5 w-5 text-slate-300" />
              <p className="eyebrow">Vista previa</p>
              <h3 className="mt-3 font-display text-2xl font-medium text-ink">
                Distribución ABC de Actividades
              </h3>
              <div className="mt-8 space-y-4">
                {[
                  ["Atención al cliente nivel 1", 28],
                  ["Resolución de incidentes técnicos", 22],
                  ["Reportes y conciliación", 18],
                  ["Gestión documental", 14],
                  ["Coordinación interna", 11],
                  ["Capacitación", 7],
                ].map(([label, val], idx) => (
                  <div key={label} className="flex items-center gap-4">
                    <span className="w-56 truncate text-[13px] text-slate-700">
                      {label}
                    </span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full ${
                          idx === 0 ? "bg-brand-gradient" : "bg-ink"
                        }`}
                        style={{ width: `${val * 2.5}%` }}
                      />
                    </div>
                    <span className="w-10 text-right font-mono text-xs text-ink">
                      {val}%
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-6 text-xs text-slate-500">
                <span className="font-medium uppercase tracking-[0.2em]">Total</span>
                <span className="font-mono text-ink">100,0%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative isolate overflow-hidden bg-gradient-hero text-slate-50">
        <div className="bg-dot-grid absolute inset-0 opacity-60" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-10 px-6 py-28 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div className="max-w-2xl">
            <p className="eyebrow text-accent-blue-light">Empiece ahora</p>
            <h2 className="mt-5 font-display text-4xl font-light leading-[1.05] tracking-[-0.02em] text-slate-50 sm:text-5xl">
              Una conversación. Un diagnóstico.{" "}
              <span className="italic text-gradient-brand">
                Su hoja de ruta IA.
              </span>
            </h2>
          </div>
          <Button
            onClick={onStart}
            disabled={loading}
            size="lg"
            className="h-14 rounded-full bg-brand-gradient px-9 text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-10px_rgba(63,124,255,0.6)] hover:opacity-95"
            data-testid="start-consulting-btn-bottom"
          >
            {loading ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : null}
            Iniciar consultoría
            <ArrowRight className="ml-3 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-8 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500 sm:flex-row sm:items-center lg:px-10">
          <InviktusLogo size={26} />
          <div>
            © {new Date().getFullYear()} · Inviktus Inc. · Multiplicador avanzado de recursos IA
          </div>
        </div>
      </footer>
    </main>
  );
}
